'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { AnyProduct } from '@cobrastyle/shared-types';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/format-price';
import { cn } from '@cobrastyle/ui';

interface StickyAddToCartProps {
  product: AnyProduct;
  onAddToCart: () => void;
  isLoading: boolean;
  disabled: boolean;
}

export function StickyAddToCart({ 
  product, 
  onAddToCart, 
  isLoading, 
  disabled 
}: StickyAddToCartProps) {
  const [isVisible, setIsVisible] = useState(false);
  const t = useTranslations('product');
  const barRef = useRef<HTMLDivElement>(null);

  // Publish how much of the bottom of the viewport this bar is occupying, so
  // anything else anchored down there — the compare tray — can sit above it
  // instead of on top of the buy button. A CSS custom property rather than
  // shared React state: the two components never have to know about each other,
  // and it is correct whichever of them happens to be mounted.
  //
  // Measured rather than hardcoded, and it self-corrects on desktop: the bar is
  // `lg:hidden`, so `offsetHeight` is 0 there and the property lands on 0px
  // even while `isVisible` is true from scrolling.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--bottom-chrome',
      isVisible ? `${barRef.current?.offsetHeight ?? 0}px` : '0px',
    );
    return () => {
      root.style.removeProperty('--bottom-chrome');
    };
  }, [isVisible]);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar after scrolling past the main add-to-cart button
      // Approximate position is around 500px on mobile
      const scrollY = window.scrollY;
      setIsVisible(scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      ref={barRef}
      className={cn(
        'fixed inset-x-0 bottom-[var(--consent-height,0px)] z-40 border-t border-border bg-background/95 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-lg transition-transform duration-300 lg:hidden',
        isVisible ? 'translate-y-0' : 'translate-y-full'
      )}
    >
      <div className="flex items-center gap-3">
        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{product.name}</p>
          {/* The page's own formatter, not the design system's <Price>: this bar
              mirrors the buy box, and it was the last place on the product page
              still quoting öre — "1 160,00 kr" under a "1 160 kr" heading. */}
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-heading text-[15px] font-bold leading-none text-accent-700">
              {formatPrice(product.price.final, product.price.currency)}
            </span>
            {product.price.discount &&
              product.price.discount.amount > 0 &&
              product.price.regular > product.price.final && (
                <span className="text-[12.5px] text-foreground/40 line-through">
                  {formatPrice(product.price.regular, product.price.currency)}
                </span>
              )}
          </span>
        </div>

        {/* Add to Cart Button */}
        <Button
          size="lg"
          onClick={onAddToCart}
          disabled={disabled}
          isLoading={isLoading}
          className="shrink-0 rounded px-6"
        >
          {t('addToCart')}
        </Button>
      </div>
    </div>
  );
}
